// Considered: AddSphereConfigureStepMolecule — rejected: it is a full generic schema-driven
// wizard (any schema, dynamic property grid, images, approaches). This form is fixed-shape
// (decision text + targetDate + owner) for one schema (center_time_action), with a parent
// (the orbit) and center (the orbit's center) already known by the caller — no picker for
// either. See spec's Meeting Decisions UI section.
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import DialogWrapper from '../DialogWrapper/DialogWrapper';
import PerspectiveSelector from '../PerspectiveSelector/PerspectiveSelector';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import type { Sphere } from 'types/global';
import './MeetingDecisionsMolecule.scss';

const TIME_ACTION_SCHEMA = 'center_time_action';

export interface MeetingDecisionsMoleculeProps {
  /** The orbit sphere decisions are parented to. Must carry a resolved center_center assignment. */
  orbitSphere: Sphere;
  organizationId: string;
  /** The current rotation instance, for analytics only — decisions still parent to the orbit, not the rotation. */
  rotationSphereId?: string;
  /** Called after a decision is successfully created. */
  onDecisionCaptured?: (sphere: Sphere) => void;
  className?: string;
}

const MeetingDecisionsMolecule: React.FC<MeetingDecisionsMoleculeProps> = ({
  orbitSphere,
  organizationId,
  rotationSphereId,
  onDecisionCaptured,
  className = '',
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const logAnalyticsEvent = useAnalytics();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [decisionText, setDecisionText] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [owner, setOwner] = useState<Sphere | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [decisions, setDecisions] = useState<Sphere[]>([]);

  const orbitCenterSphere = useMemo(
    () =>
      orbitSphere.assignedPerspectives?.find(
        (ap) => ap.perspectiveProperties.system_name === 'center_center'
      )?.assigned?.[0]?.sphere,
    [orbitSphere]
  );

  const draftSphereForPicker = useMemo(
    () =>
      ({
        id: '',
        label: TIME_ACTION_SCHEMA,
        properties: { sphereId: organizationId, type: TIME_ACTION_SCHEMA },
        assignedPerspectives: owner
          ? [
              {
                mainSphereId: '',
                perspectiveProperties: { id: '', system_name: 'center_responsible', name: 'center_responsible' },
                assigned: [{ sphere: owner, edge: { edgeId: '', urgency: 3 } }],
              },
            ]
          : [],
      }) as unknown as Sphere,
    [organizationId, owner]
  );

  const resetForm = useCallback(() => {
    setDecisionText('');
    setTargetDate('');
    setOwner(null);
    setError(null);
  }, []);

  const handleOpen = useCallback(() => {
    resetForm();
    setIsDialogOpen(true);
  }, [resetForm]);

  const handleClose = useCallback(() => {
    setIsDialogOpen(false);
    resetForm();
  }, [resetForm]);

  const handleSubmit = useCallback(async () => {
    const trimmedText = decisionText.trim();
    if (!trimmedText || !targetDate || !apiClients?.graphClient || !orbitCenterSphere || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);
    try {
      const perspectiveAssignments = [
        { perspectiveSystemName: 'center_parent', assignedSphere: orbitSphere },
        ...(owner ? [{ perspectiveSystemName: 'center_responsible', assignedSphere: owner }] : []),
      ];

      // Canonical create path — POST /spheres/init resolves center_center server-side
      // from centerSphereId (the orbit's center), then POST /spheres persists it.
      // center_parent is passed explicitly since the orbit is not the center.
      const initResp = await apiClients.graphClient.post<{ sphere?: Sphere }>('/spheres/init', {
        schemaSystemName: TIME_ACTION_SCHEMA,
        centerSphereId: orbitCenterSphere.id,
        overrides: { properties: { name: trimmedText, targetDate } },
        perspectiveAssignments,
      });

      const initializedSphere = initResp.sphere;
      if (!initializedSphere) {
        setError(t('meetingDecisions.form.captureError'));
        return;
      }

      const resp = await apiClients.graphClient.post<{ status: string; sphere?: Sphere }>('/spheres', {
        schemaSystemName: TIME_ACTION_SCHEMA,
        sphere: initializedSphere,
      });

      if (resp.sphere) {
        logAnalyticsEvent('ops_review_decision_captured', undefined, {
          orbitSphereId: orbitSphere.id,
          rotationSphereId,
        });
        setDecisions((prev) => [...prev, resp.sphere as Sphere]);
        onDecisionCaptured?.(resp.sphere);
        handleClose();
      } else {
        setError(t('meetingDecisions.form.captureError'));
      }
    } catch {
      setError(t('meetingDecisions.form.captureError'));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    decisionText,
    targetDate,
    apiClients,
    orbitCenterSphere,
    isSubmitting,
    owner,
    orbitSphere,
    organizationId,
    logAnalyticsEvent,
    rotationSphereId,
    onDecisionCaptured,
    handleClose,
    t,
  ]);

  const canSubmit = Boolean(decisionText.trim() && targetDate) && !isSubmitting;

  return (
    <div className={`meeting-decisions ${className}`.trim()} data-testid="meeting-decisions">
      <div className="meeting-decisions__header">
        <span className="meeting-decisions__title">{t('meetingDecisions.title')}</span>
        <ButtonAtom
          variant="secondary"
          size="small"
          icon="pi pi-plus"
          label={t('meetingDecisions.addDecision')}
          onClick={handleOpen}
        />
      </div>

      {decisions.length > 0 && (
        <ul className="meeting-decisions__list">
          {decisions.map((decision) => (
            <li
              key={decision.id}
              className="meeting-decisions__list-item"
              data-testid={`meeting-decision-${decision.id}`}
            >
              {String(decision.properties?.name ?? '')}
            </li>
          ))}
        </ul>
      )}

      <DialogWrapper
        header={t('meetingDecisions.form.header')}
        visible={isDialogOpen}
        onHide={handleClose}
      >
        <div className="meeting-decisions__form">
          <label className="meeting-decisions__field" htmlFor="meeting-decision-text">
            {t('meetingDecisions.form.decisionLabel')}
            <textarea
              id="meeting-decision-text"
              value={decisionText}
              onChange={(e) => setDecisionText(e.target.value)}
              disabled={isSubmitting}
            />
          </label>

          <label className="meeting-decisions__field" htmlFor="meeting-decision-target-date">
            {t('meetingDecisions.form.targetDateLabel')}
            <input
              id="meeting-decision-target-date"
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              disabled={isSubmitting}
            />
          </label>

          <div className="meeting-decisions__field">
            <PerspectiveSelector
              perspectiveSystemName="center_responsible"
              sphere={draftSphereForPicker}
              organizationId={organizationId}
              showLabel
              label={t('meetingDecisions.form.ownerLabel')}
              isEditing
              stayOpen
              onAssigneeSelected={(_id, _sid, _psn, assigneeSphere) => setOwner(assigneeSphere ?? null)}
            />
          </div>

          {error && (
            <span className="meeting-decisions__error" role="alert">
              {error}
            </span>
          )}

          <div className="meeting-decisions__actions">
            <ButtonAtom variant="ghost" label={t('common.actions.cancel')} onClick={handleClose} />
            <ButtonAtom
              variant="primary"
              label={t('meetingDecisions.form.submit')}
              onClick={handleSubmit}
              disabled={!canSubmit}
            />
          </div>
        </div>
      </DialogWrapper>
    </div>
  );
};

export default MeetingDecisionsMolecule;
